import { createContext, useContext, useState, useCallback } from 'react';
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import {
  LayoutDashboard,
  Users,
  DollarSign,
  Bell,
  UserCircle2,
  Rocket,
  Lock,
  Lightbulb,
  ChevronRight,
  MessageSquare,
  Clock,
  Star,
  TrendingUp,
  Settings,
} from 'lucide-react';

const MentorTourContext = createContext();

export const useMentorTour = () => {
  const context = useContext(MentorTourContext);
  if (!context) {
    throw new Error('useMentorTour must be used within MentorTourProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Brand tokens — pulled from the app's own UI (sidebar navy + accent red)
// ---------------------------------------------------------------------------
const COLORS = {
  navy: '#0F1830',
  navyLight: '#16213E',
  accent: '#B5433C',
  accentSoft: '#F6E9E8',
  border: '#E6E8EE',
  textBody: '#4B5563',
  textMuted: '#8A8F9B',
  bg: '#FFFFFF',
  bgAlt: '#F7F8FA',
  blue: {
    bg: '#EFF6FF',
    border: '#BFDBFE',
    text: '#1E40AF',
  },
  purple: {
    bg: '#F5F3FF',
    border: '#DDD6FE',
    text: '#6B21A8',
  },
  green: {
    bg: '#ECFDF5',
    border: '#A7F3D0',
    text: '#047857',
  },
};

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------
const IconBadge = ({ Icon }) => (
  <div
    style={{
      width: 34,
      height: 34,
      borderRadius: 8,
      background: COLORS.accentSoft,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <Icon size={17} strokeWidth={2} color={COLORS.accent} />
  </div>
);

const TourHeader = ({ icon: Icon, eyebrow, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
    {Icon && <IconBadge Icon={Icon} />}
    <div>
      {eyebrow && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: COLORS.accent,
            marginBottom: 3,
            lineHeight: 1,
          }}
        >
          {eyebrow}
        </div>
      )}
      <h3 style={{ fontSize: 18, fontWeight: 700, color: COLORS.navy, margin: 0, lineHeight: 1.3 }}>
        {title}
      </h3>
    </div>
  </div>
);

const TourBody = ({ children }) => (
  <p style={{ fontSize: 14, color: COLORS.textBody, lineHeight: 1.6, margin: '0 0 14px 0' }}>
    {children}
  </p>
);

const TourList = ({ items }) => (
  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
    {items.map((item, i) => (
      <li
        key={i}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '7px 0',
          borderTop: i === 0 ? 'none' : `1px solid ${COLORS.border}`,
          fontSize: 13.5,
          color: COLORS.textBody,
          lineHeight: 1.5,
        }}
      >
        <ChevronRight size={14} color={COLORS.accent} style={{ marginTop: 3, flexShrink: 0 }} />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const TourNote = ({ children, locked }) => (
  <div
    style={{
      marginTop: 14,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      padding: '10px 12px',
      borderRadius: 6,
      background: locked ? COLORS.bgAlt : COLORS.accentSoft,
      borderLeft: `3px solid ${locked ? COLORS.navy : COLORS.accent}`,
      fontSize: 12.5,
      color: COLORS.textBody,
    }}
  >
    {locked ? (
      <Lock size={14} color={COLORS.navy} style={{ marginTop: 1, flexShrink: 0 }} />
    ) : (
      <Lightbulb size={14} color={COLORS.accent} style={{ marginTop: 1, flexShrink: 0 }} />
    )}
    <span>
      <strong style={{ color: COLORS.navy }}>{locked ? 'Note: ' : 'Tip: '}</strong>
      {children}
    </span>
  </div>
);

const StepBadge = ({ n, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span
      style={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: COLORS.navyLight,
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {n}
    </span>
    <span style={{ fontSize: 14, color: COLORS.navy, fontWeight: 600 }}>{label}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Mentor Tour Steps
// ---------------------------------------------------------------------------
const mentorSteps = [
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Rocket} eyebrow="Welcome" title="Welcome to Mentor Dashboard" />
        <TourBody>
          Your platform for guiding students and professionals in their career journey. This walkthrough
          covers your dashboard, how to manage questions, track earnings, and connect with other mentors.
        </TourBody>
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 6,
            background: COLORS.bgAlt,
            border: `1px solid ${COLORS.border}`,
            fontSize: 13,
            color: COLORS.textMuted,
          }}
        >
          Click <strong style={{ color: COLORS.navy }}>Next</strong> to get started.
        </div>
      </div>
    ),
  },
  {
    target: '[data-tour="dashboard"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={LayoutDashboard} eyebrow="Overview" title="Your Dashboard" />
        <TourBody>This is your central hub for all mentoring activities and performance tracking.</TourBody>
        <TourList
          items={[
            <><strong style={{ color: COLORS.navy }}>Stats Overview</strong> — view answered questions, pending queries, earnings, and ratings</>,
            <><strong style={{ color: COLORS.navy }}>Question Management</strong> — manage pending and answered questions</>,
            <><strong style={{ color: COLORS.navy }}>Performance Metrics</strong> — track your impact and growth</>,
          ]}
        />
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={MessageSquare} eyebrow="Core Feature" title="Managing Questions" />
        <TourBody>
          Students and professionals ask you questions based on your expertise. Here's how to manage them effectively.
        </TourBody>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 12,
          }}
        >
          <StepBadge n={1} label="View pending questions in your dashboard" />
          <StepBadge n={2} label="Click on a question to see full details" />
          <StepBadge n={3} label="Write a detailed, helpful answer" />
          <StepBadge n={4} label="Submit your answer to help the student" />
        </div>
        <div style={{ marginTop: 12, padding: 12, background: COLORS.blue.bg, border: `1px solid ${COLORS.blue.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.blue.text, marginBottom: 6 }}>💡 Best Practices:</div>
          <TourList
            items={[
              'Provide detailed, actionable advice',
              'Share relevant examples from your experience',
              'Be encouraging and supportive',
              'Respond within 24-48 hours for better ratings',
            ]}
          />
        </div>
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Clock} eyebrow="Workflow" title="Question Tabs" />
        <TourBody>Your dashboard has two main tabs to organize your work efficiently.</TourBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ padding: 12, background: COLORS.bgAlt, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.navy, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={16} color={COLORS.accent} />
              Pending Questions
            </div>
            <TourList
              items={[
                'New questions waiting for your response',
                'Prioritize by urgency or topic',
                'Red notification badge shows count',
              ]}
            />
          </div>
          <div style={{ padding: 12, background: COLORS.bgAlt, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.navy, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageSquare size={16} color={COLORS.accent} />
              Answered Questions
            </div>
            <TourList
              items={[
                `Questions you've already answered`,
                'View student feedback and ratings',
                'Track your response history',
              ]}
            />
          </div>
        </div>
      </div>
    ),
  },
  {
    target: '[data-tour="mentors"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Users} eyebrow="Network" title="Mentors Community" />
        <TourBody>Connect with fellow mentors, view their profiles, and learn from the community.</TourBody>
        <TourList
          items={[
            'Browse other mentor profiles — see expertise and specializations',
            'View mentor ratings and reviews — learn from top performers',
            'Explore different domains — technology, healthcare, finance, and more',
            'Get inspired — see how others structure their answers',
          ]}
        />
        <TourNote>Building connections with other mentors can help you improve your mentoring skills!</TourNote>
      </div>
    ),
  },
  {
    target: '[data-tour="earnings"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={DollarSign} eyebrow="Finance" title="Earnings & Payments" />
        <TourBody>Track your earnings, view payment history, and manage your financial information.</TourBody>
        <TourList
          items={[
            <><strong style={{ color: COLORS.navy }}>Total Earnings</strong> — see your cumulative income</>,
            <><strong style={{ color: COLORS.navy }}>Payment History</strong> — track all transactions</>,
            <><strong style={{ color: COLORS.navy }}>Pending Balance</strong> — view amounts awaiting payout</>,
            <><strong style={{ color: COLORS.navy }}>Bank Details</strong> — manage payment information</>,
          ]}
        />
        <div style={{ marginTop: 12, padding: 12, background: COLORS.green.bg, border: `1px solid ${COLORS.green.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.green.text, marginBottom: 6 }}>💰 Earning Tips:</div>
          <div style={{ fontSize: 13, color: COLORS.green.text, lineHeight: 1.6 }}>
            Higher quality answers lead to better ratings, which attract more students and increase your earnings!
          </div>
        </div>
      </div>
    ),
  },
  {
    target: '[data-tour="notifications"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Bell} eyebrow="Stay Updated" title="Notifications" />
        <TourBody>Stay informed about new questions, student feedback, and important updates.</TourBody>
        <TourList
          items={[
            'New question alerts — get notified when students ask questions',
            'Rating notifications — see when students rate your answers',
            'Payment updates — track earning confirmations',
            'System announcements — stay updated on platform changes',
          ]}
        />
        <TourNote>Enable browser notifications to respond quickly and maintain high ratings!</TourNote>
      </div>
    ),
  },
  {
    target: '[data-tour="profile"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={UserCircle2} eyebrow="Account" title="Your Profile" />
        <TourBody>Manage your mentor profile, expertise, and account settings.</TourBody>
        <TourList
          items={[
            <><strong style={{ color: COLORS.navy }}>Personal Details</strong> — update contact information</>,
            <><strong style={{ color: COLORS.navy }}>Expertise Areas</strong> — add your specializations and skills</>,
            <><strong style={{ color: COLORS.navy }}>Bio & Experience</strong> — showcase your background</>,
            <><strong style={{ color: COLORS.navy }}>Profile Picture</strong> — add a professional photo</>,
            <><strong style={{ color: COLORS.navy }}>Bank Details</strong> — manage payment information</>,
          ]}
        />
        <div style={{ marginTop: 12, padding: 12, background: COLORS.purple.bg, border: `1px solid ${COLORS.purple.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.purple.text, marginBottom: 6 }}>⭐ Profile Tips:</div>
          <div style={{ fontSize: 13, color: COLORS.purple.text, lineHeight: 1.6 }}>
            A complete, professional profile with clear expertise areas attracts more students and questions!
          </div>
        </div>
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Star} eyebrow="Success" title="Building Your Reputation" />
        <TourBody>Your success as a mentor depends on the quality of your guidance and student satisfaction.</TourBody>
        <div
          style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 14,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.navy, marginBottom: 10 }}>
            Key Performance Metrics:
          </div>
          <TourList
            items={[
              <><strong style={{ color: COLORS.navy }}>Average Rating</strong> — based on student feedback (aim for 4.5+)</>,
              <><strong style={{ color: COLORS.navy }}>Response Time</strong> — faster responses lead to better ratings</>,
              <><strong style={{ color: COLORS.navy }}>Answer Quality</strong> — detailed, helpful answers get higher ratings</>,
              <><strong style={{ color: COLORS.navy }}>Total Questions</strong> — build experience and credibility</>,
            ]}
          />
        </div>
        <TourNote>Students can rate your answers from 1-5 stars and leave reviews. Focus on quality over quantity!</TourNote>
      </div>
    ),
  },
  {
    target: '[data-tour="settings"]',
    placement: 'right',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Settings} eyebrow="Customize" title="Settings" />
        <TourBody>Customize your experience and manage account preferences.</TourBody>
        <TourList
          items={[
            'Notification preferences — control what alerts you receive',
            'Privacy settings — manage your visibility',
            'Password & security — keep your account secure',
            'Language & region — customize your experience',
          ]}
        />
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center',
    content: (
      <div style={{ width: '100%' }}>
        <TourHeader icon={Rocket} eyebrow="Ready" title="You're All Set!" />
        <TourBody>Here's the recommended workflow to excel as a mentor:</TourBody>
        <div
          style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            padding: '14px 16px',
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <StepBadge n={1} label="Complete your profile with expertise and experience" />
            <StepBadge n={2} label="Check dashboard daily for pending questions" />
            <StepBadge n={3} label="Respond to questions within 24-48 hours" />
            <StepBadge n={4} label="Provide detailed, actionable guidance" />
            <StepBadge n={5} label="Track your earnings and ratings" />
            <StepBadge n={6} label="Continuously improve based on feedback" />
          </div>
        </div>
        <div style={{ marginTop: 14, padding: 12, background: COLORS.blue.bg, border: `1px solid ${COLORS.blue.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.blue.text, marginBottom: 6 }}>🎯 Success Formula:</div>
          <div style={{ fontSize: 13, color: COLORS.blue.text, lineHeight: 1.6 }}>
            Quality Answers + Quick Response + Professional Profile = More Students + Higher Earnings + Better Ratings
          </div>
        </div>
        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 12.5, color: COLORS.textMuted }}>
          You can restart this tour anytime from the help icon.
        </div>
      </div>
    ),
  },
];

// ---------------------------------------------------------------------------
// Responsive helpers
// ---------------------------------------------------------------------------
const injectResponsiveTourStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('mentor-tour-responsive-styles')) return;

  const style = document.createElement('style');
  style.id = 'mentor-tour-responsive-styles';
  style.innerHTML = `
    .react-joyride__tooltip {
      box-sizing: border-box !important;
      max-height: 85vh !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch;
    }
    .react-joyride__tooltip::-webkit-scrollbar {
      width: 6px;
    }
    .react-joyride__tooltip::-webkit-scrollbar-thumb {
      background: ${COLORS.border};
      border-radius: 3px;
    }
    @media (max-width: 640px) {
      .react-joyride__tooltip {
        width: 92vw !important;
        max-width: 92vw !important;
        padding: 16px !important;
        font-size: 13px !important;
      }
      .react-joyride__tooltip h3 {
        font-size: 16px !important;
      }
    }
    @media (max-width: 400px) {
      .react-joyride__tooltip {
        padding: 12px !important;
      }
    }
  `;
  document.head.appendChild(style);
};

export const MentorTourProvider = ({ children }) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourCompleted, setTourCompleted] = useState(() => {
    return localStorage.getItem('mentorTourCompleted') === 'true';
  });

  // Inject the responsive stylesheet once on mount.
  useState(() => {
    injectResponsiveTourStyles();
    return null;
  });

  const handleJoyrideEvent = useCallback((data) => {
    const { action, index, status, type } = data;

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
      setStepIndex(nextIndex);
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
      setStepIndex(nextIndex);
    } else if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      setStepIndex(0);
      setTourCompleted(true);
      localStorage.setItem('mentorTourCompleted', 'true');
    }
  }, []);

  const startTour = useCallback(() => {
    setRun(true);
    setStepIndex(0);
  }, []);

  const resetTour = useCallback(() => {
    setTourCompleted(false);
    localStorage.removeItem('mentorTourCompleted');
    setRun(true);
    setStepIndex(0);
  }, []);

  const value = {
    startTour,
    resetTour,
    tourCompleted,
  };

  return (
    <MentorTourContext.Provider value={value}>
      {children}
      <Joyride
        steps={mentorSteps}
        run={run}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep
        disableScrolling={false}
        onEvent={handleJoyrideEvent}
        floaterProps={{
          disableFlip: false,
          options: {
            preventOverflow: {
              rootBoundary: 'viewport',
              altBoundary: true,
              padding: 12,
              tether: false,
            },
            flip: {
              rootBoundary: 'viewport',
              altBoundary: true,
              padding: 12,
              fallbackPlacements: ['top', 'bottom', 'left', 'right', 'auto'],
            },
          },
        }}
        options={{
          primaryColor: COLORS.accent,
          zIndex: 10000,
          showProgress: true,
          showSkipButton: true,
          skipBeacon: true,
          disableOverlayClose: true,
          spotlightClicks: false,
        }}
        styles={{
          overlay: {
            backgroundColor: 'rgba(15, 24, 48, 0.55)',
          },
          tooltip: {
            borderRadius: 10,
            fontSize: 15,
            width: 'min(560px, 92vw)',
            maxWidth: '92vw',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            padding: 'clamp(14px, 4vw, 26px)',
            boxShadow: '0 12px 32px rgba(15, 24, 48, 0.18)',
          },
          tooltipContainer: {
            textAlign: 'left',
          },
          tooltipContent: {
            padding: '0',
          },
          tooltipFooter: {
            marginTop: 18,
            paddingTop: 16,
            borderTop: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          },
          tooltipFooterSpacer: {
            flex: 1,
          },
          buttonNext: {
            backgroundColor: COLORS.navyLight,
            borderRadius: 6,
            padding: '9px 18px',
            fontSize: '13.5px',
            fontWeight: '600',
            boxShadow: 'none',
            outline: 'none',
          },
          buttonBack: {
            color: COLORS.navy,
            marginRight: 10,
            padding: '9px 14px',
            fontSize: '13.5px',
            fontWeight: '600',
            outline: 'none',
          },
          buttonSkip: {
            color: COLORS.textMuted,
            fontSize: '13px',
            padding: '9px 4px',
            outline: 'none',
          },
          buttonClose: {
            display: 'none',
          },
          options: {
            arrowColor: '#fff',
          },
        }}
        locale={{
          back: 'Previous',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip Tour',
        }}
      />
    </MentorTourContext.Provider>
  );
};
